// src/com/trans_plan_tsr/TransPlanTsrItem.js
import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/system';
import { styled } from '@mui/material/styles';
import { Button, IconButton, Tooltip, Chip } from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront'; // 동적표시용.
import LocationOnIcon from '@mui/icons-material/LocationOn'; // 지도로 이동
import LocationOffIcon from '@mui/icons-material/LocationOff'; // 위치정보 없음
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client';
import { useSnackbar } from 'notistack';
import { useConfirmDialog } from '../../common/AppConfirmDialog';
import {
    AppMenuUrl, AppObject, AppStyle, AppWord, MapValue, TopCode
} from '../../value/AppConst';
import { useAlertSetter, useAuthorizationCheck } from '../../api/app-hook';
import {
    authTableTreeRepo, codeTreeRepo, dcInfoListRepo, menuRequestRepo, menuTreeRepo,
    ShowDebugRepo, transPlanLaunchListParamRepo, userInfoRepo
} from '../../value/AppModel';
import TransPlanTsrEditorGroup, {
    TabCodeForTsrEditorGroup
} from './TransPlanTsrEditorGroup';
import {
    GET_TRANS_PLAN_TSR, ISSUE_TRANS_PLAN_TSR, REV_ISSUE_TRANS_PLAN_TSR,
    SAVE_TRANS_PLAN_TSR
} from './TransPlanTsrGql';
import { TransPlanTsrDictionary } from './TransPlanTsrDict';
import ValueMisc from '../../value/ValueMisc';
import IsValid from '../../value/IsValid';
import {
    SEND_SMS_TRANS_PLAN_FOR_DEPART, SEND_SMS_TRANS_PLAN_FOR_READY
} from '../trans_plan/TransPlanGql';
import { WhiteChip } from '../../common/VzmStyled';

const HochaContainer = styled(Box)(({ theme }) => ({
    display: 'inline-flex',
    width: 200, minHeight: 580,
    flexDirection: 'column',
    padding: theme.spacing(1),
    transition: 'all 0.3s ease',
    fontSize: '0.8rem',
    backgroundColor: '#eee',
    border: AppStyle.BorderCCC,
    margin: '5px 3px 1px 3px',
    '&:hover': {
        transform: 'scale(1.01)',
        border: '1px solid ' + AppStyle.PrimaryRGB,
        borderRadius: 5,
        '& *': {
            transition: 'all 0.3s ease',
            fontSize: 'calc(100%+ 1px)',
        },
    }
}));

const EmptyHocha = styled(Box)({
    display: 'inline-flex', width: 200, minHeight: 580, flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
});

// store popover
const StoreLineBox = styled(Box)({ display: 'flex', alignItems: 'center', padding: 5 });

const TitleBox = styled(Box)({ fontWeight: 'bold', textAlign: 'center', padding: 5 });

const DataBox = styled(Box)({
    backgroundColor: 'white', padding: 5,
});

const LabelAndTextBox = styled(Box)({
    padding: '5px 0px 5px 5px', display: 'flex', alignItems: 'center', minHeight: 14
});
const LabelBox = styled(Box)({ fontSize: '0.6rem' });
const TextBox = styled(Box)({ flexGrow: 1, paddingRight: 5 });
const LabelAndText = ({
    label,
    text,
    hover
}) => {
    return (
        <LabelAndTextBox>
            <TextBox>{text || <span>&nbsp;</span>}</TextBox>
            <LabelBox style={{ color: hover ? AppStyle.PrimaryRGB : 'transparent' }}>{label}</LabelBox>
        </LabelAndTextBox>
    )
};

const printDateTime = (time) => {
    if (time && time.length > 10) {
        return time.substring(5, 16).replace('-', '/');
    }
    return '';
}
/* const WORD_OFF = 'off';

function getDefaultTemp(dcInfoListData, codeTree, item) {
        const dcInfo = dcInfoListData.records.find(dc => dc.dcCd === item.dcCd);
        if(dcInfo) {
            if(dcInfo.dcTypeCd === 'E' && IsValid.string(item.transTypeCd, 1, 5)) {
                const transTypes = ValueMisc.getCodes(codeTree, CodeGroup.TRANS_TYPE_CD);
                const tt = transTypes.find(t => t.codeId === item.transTypeCd);
                if(tt) {
                    return {
                        fore: tt.code1,
                        middle: tt.code2,
                        rear: tt.code3
                    }
                }
            }
            return {
                fore: dcInfo.tempFore || WORD_OFF,
                middle: dcInfo.tempMid || WORD_OFF,
                rear: dcInfo.tempRear || WORD_OFF
            }
        }
        return {
            fore: WORD_OFF,
            middle: WORD_OFF,
            rear: WORD_OFF
        }
} */

/**
 * TSR 발행 화면(TransPlanTsr)에서 각 호차 정보를 보여주는 컴포넌트.
 * DC 사용자와 점포 사용자가 보는 버튼이 다르다.
 * 점포 사용자는 편집, 저장이 안됨.
 * @param {*} param0 
 */
export default function TransPlanTsrItem({
    item, // item of LIST_TRANS_PLAN_TSR. {hocha, planId, dcCd, dcName, storeId, storeName,... bulkItems:{}}
    onCompleteMutation,
}) {
    const sessionInfo = useReactiveVar(userInfoRepo);
    const codeTree = useReactiveVar(codeTreeRepo);
    const showDebug = useReactiveVar(ShowDebugRepo);
    const menuTree = useReactiveVar(menuTreeRepo);
    const authTableTree = useReactiveVar(authTableTreeRepo);
    const dcInfoListData = useReactiveVar(dcInfoListRepo); // 기본온도 가져오기.
    const [hovered, setHovered] = useState(false);
    const [sentSmsToReady, setSentSmsToReady] = useState(false);
    const [editorState, setEditorState] = useState({ open: false, tab: 0, data: null, changed: false });
    const [goodMobile, setGoodMobile] = useState(false);
    const [daysOld, setDaysOld] = useState(0);
    const [storesBound, setStoresBound] = useState(false);
    const openConfirmDialog = useConfirmDialog();
    const addAlert = useAlertSetter();
    const { enqueueSnackbar } = useSnackbar();
    const checkAuth = useAuthorizationCheck();
    const tabRef = useRef(0); // data 가져온 후 editor group 열 때 선택할 tab을 임시 저장.
    const deviceButtonRef = useRef(null);
    const shouldFocusSaveButtonRef = useRef(false);
    const [getTransPlanTsrItem, responseItem] = useLazyQuery(GET_TRANS_PLAN_TSR, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => { onCompleteGetTransPlanTsrItem(data, option) },
        onError: (error) => addAlert({ title: '배차정보 조회 오류', message: [error.message] })
    });
    const [saveTransPlanTsr, responseSaveTsr] = useMutation(SAVE_TRANS_PLAN_TSR, {
        onCompleted: (data, option) => onCompleteSaveTsr(data, option),
        onError: (error) => addAlert({ title: TransPlanTsrDictionary.errorTitle.Save, message: [error.message] })
    });
    const [issueTransPlanTsr, responseIssueTsr] = useMutation(ISSUE_TRANS_PLAN_TSR, {
        onCompleted: (data, option) => onCompleteIssueTsr(data, option),
        onError: (error) => addAlert({ title: 'TSR 발행 오류', message: [error.message] })
    });
    const [reverseIssueTransPlanTsr, responseReverseIssueTsr] = useMutation(REV_ISSUE_TRANS_PLAN_TSR, {
        onCompleted: (data, option) => onCompleteReverseIssueTsr(data, option),
        onError: (error) => addAlert({ title: 'TSR 발행 취소 오류', message: [error.message] })
    });
    const [sendSmsForReady, responseSendSms] = useMutation(SEND_SMS_TRANS_PLAN_FOR_READY, {
        onCompleted: (data, option) => onCompleteSendSmsForReady(data, option),
        onError: (error) => addAlert({ title: '출차준비 메시지 전송 오류', message: [error.message] })
    });
    const [sendSmsForDeparture, responseSendSmsForDeparture] = useMutation(SEND_SMS_TRANS_PLAN_FOR_DEPART, {
        onCompleted: (data, option) => onCompleteSendSmsForDeparture(data, option),
        onError: (error) => addAlert({ title: '출차메시지 전송 오류', message: [error.message] })
    });

    useEffect(() => {
        setEditorState({ open: false, tab: 0, data: null, changed: false });
        const isGood = IsValid.mobile(item.driverMobile);
        setGoodMobile(isGood);
        const planDate = new Date(item.planDate);
        const today = new Date();
        setDaysOld(Math.floor((today - planDate) / 86400000));
        setStoresBound(item.bindStores && item.bindStores.length > 0);
        if (!isGood) setSentSmsToReady(true); // 전화번호가 잘못된 경우는 어차피 보낼 수 없으므로 true로 설정.
    }, [item]);

    useEffect(() => {
        if (!editorState.open && shouldFocusSaveButtonRef.current) {
            shouldFocusSaveButtonRef.current = false;
            window.requestAnimationFrame(() => {
                if (deviceButtonRef.current && !deviceButtonRef.current.disabled) {
                    deviceButtonRef.current.focus();
                }
            });
        }
    }, [editorState.open]);

    const onCompleteGetTransPlanTsrItem = (data, option) => {
        if (data.transPlanTsrItem) {
            /*
             디폴트 데이터로 일부 데이터를 수정해 줘야 함.
             - Express DC의 경우, transTypeCd에 따라 tempF, tempM, tempR를 설정해 줘야 함.
            */
            const defaultTemp = ValueMisc.getTsrDefaultTemp(dcInfoListData, codeTree, data.transPlanTsrItem);
            const newData = { ...data.transPlanTsrItem };

            if(!newData.tempF) newData.tempF = defaultTemp.fore;
            if(!newData.tempM) newData.tempM = defaultTemp.middle;
            if(!newData.tempR) newData.tempR = defaultTemp.rear;
            // 기타 몇가지 시간들.
            const ymdHms = ValueMisc.getYmdHms(new Date());
            if(!newData.prosDptrTime) newData.prosDptrTime = newData.dptrReqTime || ymdHms;
            if(!newData.prosArrTime) newData.prosArrTime = newData.arrReqTime || ymdHms;
            if(!newData.loadStTime) newData.loadStTime = ymdHms;
            if(!newData.loadEnTime) newData.loadEnTime = ymdHms;

            setEditorState({ open: true, data: newData, changed: false, tab: tabRef.current });
        }
        else addAlert({ title: '배차정보 조회 오류', message: ['배차정보를 찾을 수 없습니다.'] });
    };

    const onCompleteSaveTsr = (data, clientOption) => {
        if (data.transPlanTsrSave.ok) {
            const newState = { ...editorState, changed: false, data: null };
            setEditorState(newState);
            enqueueSnackbar('변경사항을 저장했습니다.', { variant: 'success', autoHideDuration: 1600 });
            onCompleteMutation();
        }
        else addAlert({ title: TransPlanTsrDictionary.errorTitle.Save, message: [data.transPlanTsrSave.message] });
    };

    const onCompleteIssueTsr = (data, clientOption) => {
        if (data.transPlanTsrIssue.ok) {
            enqueueSnackbar('TSR을 발행했습니다.', { variant: 'success', autoHideDuration: 1600 });
            popTsrPrintWindow();
            onCompleteMutation();
        }
        else addAlert({ title: 'TSR 발행 오류', message: [data.transPlanTsrIssue.message] });
    };

    const onCompleteReverseIssueTsr = (data, clientOption) => {
        if (data.transPlanTsrRevIssue.ok) {
            enqueueSnackbar('TSR 발행을 취소했습니다.', { variant: 'success', autoHideDuration: 1600 });
            onCompleteMutation();
        }
        else addAlert({ title: 'TSR 발행 취소 오류', message: [data.transPlanTsrRevIssue.message] });
    };

    const onCompleteSendSmsForReady = (data, clientOption) => {
        if (data.transPlanSendSmsForReady.ok) {
            enqueueSnackbar('출차 준비 메시지를 전송했습니다.', { variant: 'success', autoHideDuration: 1600 });
            setSentSmsToReady(true);
        }
        else addAlert({ title: '출차메시지 전송 오류', message: [data.transPlanSendSmsForReady.message] });
    };

    const onCompleteSendSmsForDeparture = (data, clientOption) => {
        if (data.transPlanSendSmsForDepart.ok) {
            enqueueSnackbar('출차 메시지를 전송했습니다.', { variant: 'success', autoHideDuration: 1600 });
        }
        else addAlert({ title: '출차메시지 전송 오류', message: [data.transPlanSendSmsForDepart.message] });
    };

    const getTransPlanTsrItemOrOpenEditor = (tab) => {
        if (editorState.data) {
            // open editor
            setEditorState({ ...editorState, open: true, tab: tab });
        }
        else {
            // get data
            tabRef.current = tab;
            getTransPlanTsrItem({ variables: { planId: item.planId } });
        }
    }

    const onClickGoToMap = () => {
        if (item.gpsTime) {
            // 지도로 이동
            const mapUrl = ValueMisc.makeMapUrl({
                job: MapValue.MapInitEvent.traceVehicle,
                vehInfoSeqToTrace: item.vehInfoSeq,
                vehPlates: item.vehPlates,
                vehAlias: item.vehPlates
            });
            window.open(mapUrl, AppWord.MAP_WINDOW_NAME);
        }
    };

    const onClickSendReadyMsg = async () => {
        const confirm = await openConfirmDialog({
            title: "출차 준비 메시지 전송",
            content: [
                `${item.hocha}호차의 출차 준비 메시지를 전송합니다.`,
                `기사: ${item.driverName}, 연락처: ${item.driverMobile}`,
                (daysOld > 1 ? '하루 이상 경과된 건 입니다. ' : '') + '계속하시겠습니까?'
            ]
        });
        if (confirm) sendSmsForReady({ variables: { planId: item.planId } });
        else setSentSmsToReady(true); // 실제는 보내지 않았으나 거부한 경우도 보낸 것으로 처리.
    };

    const onClickFakeSendMsg = async () => {
        const confirm = await openConfirmDialog({
            title: "출차 준비 메시지 전송(Fake)",
            content: [
                `${item.hocha}호차의 출차 준비 메시지를 전송합니다.`,
                `기사: ${item.driverName}, 연락처: ${item.driverMobile}`,
                (daysOld > 1 ? '하루 이상 경과된 건 입니다. ' : '') + '계속하시겠습니까?'
            ]
        });
        if (confirm) {
            console.log('Fake Send Ready Msg for planId=' + item.planId);
            setSentSmsToReady(true);
        }
        // else setSentSmsToReady(true); // 실제는 보내지 않았으나 거부한 경우도 보낸 것으로 처리.
    };

    const onClickSetDetail = () => {
        getTransPlanTsrItemOrOpenEditor(TabCodeForTsrEditorGroup.DetailEditor);
    };

    const onClickSetSupplies = () => {
        getTransPlanTsrItemOrOpenEditor(TabCodeForTsrEditorGroup.SupplyEditor);
    };

    // 상차 상세 내역이나 물류집기 변경 요청
    const onRequestSaveInMemory = (data) => {
        shouldFocusSaveButtonRef.current = true;
        const newState = { ...editorState, data: data, changed: true, open: false };
        setEditorState(newState);
    };

    const popTsrPrintWindow = () => {
        const url = ValueMisc.makeTsrUrl({ planId: item.planId });
        //window.open(url, AppWord.TSR_PRINT_WINDOW_NAME);
        // features 없이 열면 브라우저가 새 창이 아닌 새 탭으로 열어버려서,
        // 탭을 닫아도 opener 쪽 activeElement가 되돌아오지 않고 window.focus()도
        // 먹히지 않아 클릭은 되어도 키보드 입력이 씹히는 문제가 있었다.
        // MapMenuAndWatch의 팝업(popMapWindow)처럼 실제 별도 창으로 띄운다.
        const width = window.innerWidth;
        const height = window.innerHeight;
        const printWindow = window.open(
            url, AppWord.TSR_PRINT_WINDOW_NAME,
            MapValue.PopupOption + `,width=${width},height=${height}`
        );

        // 팝업을 닫은 뒤 opener 쪽으로 포커스가 자동 복귀되지 않는 브라우저가 있어,
        // 팝업이 닫히는 걸 감지해서 원래 포커스였던 요소로 명시적으로 되돌려 준다.
        if (printWindow) {
            const elementToRefocus = document.activeElement;
            const checkClosedInterval = setInterval(() => {
                if (printWindow.closed) {
                    clearInterval(checkClosedInterval);
                    if (elementToRefocus && typeof elementToRefocus.focus === 'function') {
                        elementToRefocus.focus();
                    }
                    else {
                        window.focus();
                    }
                }
            }, 300);
        }
    };

    /**
     * TSR 저장 && TSR 미발행이면 TSR 발행한 후 출력하고
     * TSR 발행이면 TSR 출력만 한다.
     */
    const onClickPrintTsr = () => {
        if (item.reqStateFlag === TopCode.ReqStateFlag.TsrNotIssued) {
            // == '1'
            issueTransPlanTsr({ variables: { planId: item.planId } });
        }
        else if (item.reqStateFlag === TopCode.ReqStateFlag.TsrIssued) {
            // == '2'
            popTsrPrintWindow();
        }
    };

    const onClickOnlyPrintTsr = () => {
        if (item.reqStateFlag === TopCode.ReqStateFlag.TsrIssued) popTsrPrintWindow();
    };

    // 출차 메시지 전송. TSR 발행 후에만 가능.
    const onClickSendMessageForDeparture = async () => {
        const confirm = await openConfirmDialog({
            title: "출차 메시지 전송",
            content: [
                `${item.hocha}호차의 출차 메시지를 전송합니다.`,
                `기사: ${item.driverName}, 연락처: ${item.driverMobile}`,
                '계속하시겠습니까?'
            ]
        });
        if (confirm) sendSmsForDeparture({ variables: { planId: item.planId } });
    };

    // 배차요청 화면으로 이동하기.
    const onClickToMoveToTransPlanRequest = () => {
        if (sessionInfo.isHomeplusOrEtrace()) {
            const menuSet = ValueMisc.findMenu(AppMenuUrl.TransPlan, menuTree);
            if (!menuSet.menu) {
                addAlert({
                    title: '운송요청 화면 이동',
                    message: ['운송요청 화면이 없습니다. 관리자에게 문의해 주세요.'],
                });
                return;
            }
            transPlanLaunchListParamRepo({
                planDate: item.planDate,
                storeId: item.storeId,
            });
            menuRequestRepo(menuSet);
        }
    };

    const onRequestCloseGroupEditor = () => {
        shouldFocusSaveButtonRef.current = true;
        const newState = { ...editorState, open: false };
        setEditorState(newState);
    }

    // detailSaved에 저장되어 있는 변경사항을 실제로 저장
    const onClickSave = () => {
        // 변경되지 않았어도 그냥 저장버튼을 눌러 저장할 수 있도록 해달라고.
        // 그래야 TSR 발행을 TSR 미발행 상태로 되돌릴 수 있기 때문임.
        if (editorState.changed) {
            if(!editorState.data) {
                enqueueSnackbar('데이터가 준비되지 않았습니다.', { variant: AppStyle.mui.warning, autoHideDuration: 1500 });
                return;
            }
            // type별로 서로 다른 mutation 호출할 것인가, 아니면 하나로 통합할 것인가?
            const data = {
                planId: item.planId,
                // Detail part
                bol1: editorState.data.bol1,
                bol2: editorState.data.bol2,
                bol3: editorState.data.bol3,
                loadCount: editorState.data.loadCount,
                loadStTime: editorState.data.loadStTime,
                loadEnTime: editorState.data.loadEnTime,
                prosDptrTime: editorState.data.prosDptrTime,
                prosArrTime: editorState.data.prosArrTime,
                sealNo1: editorState.data.sealNo1,
                sealNo2: editorState.data.sealNo2,
                sealNo3: editorState.data.sealNo3,
                loadStaffName: editorState.data.loadStaffName,
                unsoldBol: editorState.data.unsoldBol,
                unsoldPallet: editorState.data.unsoldPallet,
                unsoldCount: editorState.data.unsoldCount,
                memo: editorState.data.memo,
                // Supply part(상온)
                aPalKpp: editorState.data.aPalKpp,
                aPalAju: editorState.data.aPalAju,
                aRollN: editorState.data.aRollN,
                aRollJum: editorState.data.aRollJum,
                aB2bKpp: editorState.data.aB2bKpp,
                aB2bAju: editorState.data.aB2bAju,
                aEtc: editorState.data.aEtc,
                aOpSpec: editorState.data.aOpSpec,
                aOpJum: editorState.data.aOpJum,
                aOpTrunc: editorState.data.aOpTrunc,
                aHiDanpra: editorState.data.aHiDanpra,
                aHiRoll: editorState.data.aHiRoll,
                aTray: editorState.data.aTray,
                bulkItemList: editorState.data.bulkItemList, // list. (982 only)
                // fresh
                tempF: editorState.data.tempF,
                fFPalKpp: editorState.data.fFPalKpp,
                fFPalAju: editorState.data.fFPalAju,
                fFShrFull: editorState.data.fFShrFull,
                fFShrHalf: editorState.data.fFShrHalf,
                fFEtcKpp: editorState.data.fFEtcKpp,
                fFEtcAju: editorState.data.fFEtcAju,
                tempM: editorState.data.tempM,
                fMPalKpp: editorState.data.fMPalKpp,
                fMPalAju: editorState.data.fMPalAju,
                fMShrFull: editorState.data.fMShrFull,
                fMShrHalf: editorState.data.fMShrHalf,
                fMEtcKpp: editorState.data.fMEtcKpp,
                fMEtcAju: editorState.data.fMEtcAju,
                tempR: editorState.data.tempR,
                fRPalKpp: editorState.data.fRPalKpp,
                fRPalAju: editorState.data.fRPalAju,
                fRShrFull: editorState.data.fRShrFull,
                fRShrHalf: editorState.data.fRShrHalf,
                fREtcKpp: editorState.data.fREtcKpp,
                fREtcAju: editorState.data.fREtcAju,
                fNjc: editorState.data.fNjc,
                fUcolor: editorState.data.fUcolor,
                fOctagon: editorState.data.fOctagon,
                fTray: editorState.data.fTray,
                // express
                //tempF: detailSaved.data.tempF,
                eFDolly: editorState.data.eFDolly,
                eFDollycart: editorState.data.eFDollycart,
                eFRoll: editorState.data.eFRoll,
                eFPalKpp: editorState.data.eFPalKpp,
                eFPalAju: editorState.data.eFPalAju,
                //tempM: detailSaved.data.tempM,
                eMDolly: editorState.data.eMDolly,
                eMDollycart: editorState.data.eMDollycart,
                eMRoll: editorState.data.eMRoll,
                eMPalKpp: editorState.data.eMPalKpp,
                eMPalAju: editorState.data.eMPalAju,
                //tempR: detailSaved.data.tempR,
                eRDolly: editorState.data.eRDolly,
                eRDollycart: editorState.data.eRDollycart,
                eRRoll: editorState.data.eRRoll,
                eRPalKpp: editorState.data.eRPalKpp,
                eRPalAju: editorState.data.eRPalAju,
                eCigar: editorState.data.eCigar,
                eCorn: editorState.data.eCorn,
                ePulp: editorState.data.ePulp,
                eMu: editorState.data.eMu,
                eJumDolly: editorState.data.eJumDolly,
                eJumDollycart: editorState.data.eJumDollycart,
                eJumRoll: editorState.data.eJumRoll,
            };
            saveTransPlanTsr({ variables: { transPlanTsr: data } });
        }
        else {
            if(item && item.planId) {
                // TSR 발행 취소. (item.reqStateFlag === TopCode.ReqStateFlag.TsrIssued 검사x)
                reverseIssueTransPlanTsr({ variables: { planId: item.planId } });
            }
        }
    };

    const okToSave = checkAuth(authTableTree.tree.pr_trans_plan_tsr_save);
    const okToSendReadyMsg = checkAuth(authTableTree.tree.pr_trans_plan_sms_for_ready);
    const okToSendLeaveMsg = checkAuth(authTableTree.tree.pr_trans_plan_sms_to_go);

    const canSendReadyMsg // 출차준비메세지 전송 가능
        = okToSendReadyMsg && item.dcUseYn === 'Y'
        && item.reqStateFlag !== TopCode.ReqStateFlag.TsrIssued
        && item.transFlag;

    const canSetDetail // 상차상세내역, 물류집기 설정 가능
        = !sessionInfo.isHomeplusOrEtrace() // 점포사용자(사실상)는 열어볼 수 있다 (다만 저장은 안되도록 되어 있다.)
        || (
            okToSave
            && (
                sentSmsToReady || 
                Boolean(item.loadStTime)
                || item.reqStateFlag !== TopCode.ReqStateFlag.NewRequest
            )
            && item.transFlag
        );

    const canSave
        = okToSave && item.dcUseYn === 'Y'
        && (
            sentSmsToReady ||
            Boolean(item.loadStTime)
            || item.reqStateFlag !== TopCode.ReqStateFlag.NewRequest)
        && item.transFlag;

    const canPrintTsr
        = (sentSmsToReady || Boolean(item.loadStTime) || item.reqStateFlag !== TopCode.ReqStateFlag.NewRequest)
        && item.transFlag; // transFlg : 배차 여부.

    // 출차 메시지
    const canSendMessageToLeave
        = (sentSmsToReady || Boolean(item.loadStTime) || item.reqStateFlag === TopCode.ReqStateFlag.TsrIssued)
        && okToSendLeaveMsg
        && goodMobile
        && item.transFlag;

    const renderBindStores = () => {
        const stores = item.bindStores;
        if (stores && stores.length > 0) {
            return (
                <Box style={{ padding: 5 }}
                >
                    <Tooltip
                        title={
                            <Box>
                                {
                                    stores.map((store) => {
                                        return (
                                            <StoreLineBox key={store.storeId}
                                                style={{ fontSize: '0.9rem' }}
                                            >
                                                <StorefrontIcon
                                                    fontSize={AppWord.SMALL}
                                                    color={
                                                        store.storeId === item.storeId
                                                            ? AppStyle.mui.inherit
                                                            : AppStyle.mui.warning
                                                    }
                                                />
                                                <Box style={{ paddingLeft: 5 }}>
                                                    [{store.storeId}] {store.storeName}
                                                </Box>
                                            </StoreLineBox>
                                        )
                                    })
                                }
                            </Box>
                        }
                    >
                        <Box style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {
                                stores.map((store) => {
                                    return (
                                        <React.Fragment key={store.storeId}> ..
                                            <StorefrontIcon fontSize={AppWord.SMALL}
                                                color={
                                                    store.storeId === item.storeId
                                                        ? AppStyle.mui.inherit
                                                        : AppStyle.mui.warning
                                                }
                                            />
                                        </React.Fragment>
                                    )
                                })
                            }
                        </Box>
                    </Tooltip>
                </Box>
            );
        }
        return (
            <Box style={{ padding: 5, display: 'flex', alignItems: 'center' }}>
                <StorefrontIcon fontSize={AppWord.SMALL} color={AppStyle.mui.disabled} /> 동적 점포 없음
            </Box>
        )
    };

    const loadingAny
        = responseItem.loading
        || responseSaveTsr.loading
        || responseIssueTsr.loading
        || responseSendSms.loading
        || responseSendSmsForDeparture.loading
        || responseReverseIssueTsr.loading;

    const onClickDebug = () => {
        if (!item) {
            console.log('item is null');
            return;
        }

        /* addAlert({
            title: 'Debug data',
            message: ['콘솔화면을 보세요.'],
        }); */

        console.log("### Debug Info for TransPlanTsrItem ###");
        console.log('okToSave', okToSave);
        console.log('okToSendReadyMsg', okToSendReadyMsg);
        console.log('okToSendLeaveMsg', okToSendLeaveMsg);
        // 아래 disabled에 사용되는 모든 값들을 console.log로 찍어보자.
        console.log('canSendReadyMsg', canSendReadyMsg);
        console.log('canSetDetail', canSetDetail);
        console.log('canSave', canSave);
        console.log('canPrintTsr', canPrintTsr);
        console.log('canSendMessageToLeave', canSendMessageToLeave);
        console.log('item.loadStTime', item.loadStTime);
        console.log('item.reqStateFlag', item.reqStateFlag);
        console.log('item.transFlag', item.transFlag);
        console.log('sentSmsToReady', sentSmsToReady);
        console.log('item.loadStTime', item.loadStTime);
        console.log('item:', item);
    };

    if(!item.hocha) {
        return (
            <HochaContainer style={{ border: 0, boxShadow: 'none' }}>
                <Box style={{
                    flexGrow: 1,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    height: '100%', backgroundColor: '#f00'
                }}>
                    <LocalShippingIcon fontSize={AppWord.LARGE} color={AppStyle.mui.disabled} />
                </Box>
            </HochaContainer>
        );
    }

    return (
        <HochaContainer
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            <TitleBox
                style={{ color: hovered ? AppStyle.PrimaryRGB : 'black' }}
            >
                {
                    showDebug &&
                    <Chip label='d' size={AppWord.SMALL} style={{ float: 'left' }}
                        onClick={onClickDebug}
                    />
                }
                <Tooltip title={`${item.planId}(${item.reqStateFlag})`}>
                    <WhiteChip
                        label={item.hocha + "호차"}
                        size={AppWord.SMALL}
                    />
                </Tooltip>
                {
                    item.reqStateFlag === TopCode.ReqStateFlag.TsrNotIssued ?
                        <Tooltip title={
                            sessionInfo.isHomeplusOrEtrace()
                                ? "운송요청으로 이동" : "TSR이 아직 발행되지 않았습니다."}>
                            <Chip
                                label="TSR미발행"
                                size={AppWord.SMALL}
                                onClick={onClickToMoveToTransPlanRequest}
                                color={AppStyle.mui.warning}
                                style={{ marginLeft: 5, fontSize: '0.7rem', fontWeight: 'normal' }}
                            />
                        </Tooltip>
                        :
                        item.reqStateFlag === TopCode.ReqStateFlag.TsrIssued ?
                            <Tooltip title={
                                sessionInfo.isHomeplusOrEtrace()
                                    ? "운송요청으로 이동" : "TSR을 출력할 수 있습니다."}>
                                <Chip
                                    label="TSR발행"
                                    size={AppWord.SMALL}
                                    onClick={onClickToMoveToTransPlanRequest}
                                    color={AppStyle.mui.success}
                                    style={{ marginLeft: 5, fontSize: '0.7rem', fontWeight: 'normal' }}
                                />
                            </Tooltip>
                            : null
                }
                {
                    storesBound &&
                    <Chip label="동" size={AppWord.SMALL}
                        color={AppStyle.mui.error}
                        style={{ marginLeft: 5, fontSize: '0.7rem', fontWeight: 'normal' }}
                    />
                }
            </TitleBox>
            <DataBox>
                <LabelAndText label="배차구분" text={item.transType || '-'} hover={hovered} />
                <LabelAndText label="차종" text={item.vehTypeName} hover={hovered} />
                <LabelAndText label="운송사" text={item.comName} hover={hovered} />
                <LabelAndText label="접안시간" text={printDateTime(item.shprReqTime)} hover={hovered} />
                <LabelAndText label="출발요청시간" text={printDateTime(item.dptrReqTime)} hover={hovered} />
                <LabelAndText label="도착요청시간" text={printDateTime(item.arrReqTime)} hover={hovered} />
                <LabelAndText label="Dock No." text={item.dockno || '-'} hover={hovered} />
                <LabelAndText label="추가운임" text={item.addCharge || '-'} hover={hovered} />
                <LabelBox style={{ color: '#777', marginTop: 5 }}>비고</LabelBox>
                <Box
                    style={{
                        padding: 5, border: AppStyle.BorderEEE, borderRadius: 5, height: 40,
                        overflow: 'auto', whiteSpace: 'pre-wrap'
                    }}
                >
                    {item.note}
                </Box>
                {renderBindStores()}
            </DataBox>
            <Box
                style={{
                    padding: 5,
                    backgroundColor: '#cef',
                    borderRadius: 5,
                }}
            >
                <LabelAndTextBox>
                    <TextBox>{item.vehPlates}</TextBox>
                    <LabelBox style={{ color: hovered ? AppStyle.PrimaryRGB : 'transparent' }}>
                        <Tooltip title={item.gpsTime ? "지도로 이동" : "위치정보 없음"}>
                            <IconButton
                                size={AppWord.SMALL}
                                onClick={onClickGoToMap}
                                color={AppStyle.mui.secondary}
                            >
                                {
                                    item.gpsTime ?
                                        <LocationOnIcon fontSize='11pt' /> :
                                        <LocationOffIcon fontSize='11pt' />
                                }
                            </IconButton>
                        </Tooltip>
                    </LabelBox>
                </LabelAndTextBox>
                <LabelAndText label="기사" text={item.driverName} hover={hovered} />
                <LabelAndText label="연락처" text={item.driverMobile} hover={hovered} />
            </Box>
            {
                sessionInfo.isHomeplusOrEtrace() &&
                <Button
                    style={{ marginTop: 5 }}
                    size={AppWord.SMALL}
                    variant={AppStyle.mui.outlined}
                    color={AppStyle.mui.success}
                    disabled={!canSendReadyMsg || !goodMobile || loadingAny}
                    onClick={onClickSendReadyMsg}
                >
                    출차 준비 메시지
                </Button>
            }
            {
                showDebug &&
                <Button
                    style={{ marginTop: 5 }}
                    size={AppWord.SMALL}
                    variant={AppStyle.mui.outlined}
                    color={AppStyle.mui.primary}
                    disabled={!canSendReadyMsg || !goodMobile || loadingAny}
                    onClick={onClickFakeSendMsg}
                >
                    Fake 준비 메시지
                </Button>
            }
            <Button
                style={{ marginTop: 5 }}
                size={AppWord.SMALL}
                variant={AppStyle.mui.outlined}
                color={AppStyle.mui.primary}
                disabled={!canSetDetail || loadingAny}
                onClick={onClickSetDetail}
            >
                상차 상세 내역
            </Button>
            <Button
                ref={deviceButtonRef}
                style={{ marginTop: 5 }}
                size={AppWord.SMALL}
                variant={AppStyle.mui.outlined}
                color={AppStyle.mui.secondary}
                disabled={!canSetDetail || loadingAny}
                onClick={onClickSetSupplies}
            >
                물류집기
            </Button>
            {
                !sessionInfo.isHomeplusOrEtrace() &&
                <Button
                    size={AppWord.SMALL}
                    style={{ marginTop: 5 }}
                    variant={AppStyle.mui.contained}
                    color={AppStyle.mui.success}
                    disabled={!canPrintTsr || loadingAny}
                    onClick={onClickOnlyPrintTsr}
                >
                    TSR
                </Button>
            }
            {
                /*  수시로 정보 수정이 필요. 임시저장 및 TSR저장 버튼은 항시 활성화 바람. 2026.6.24 */
                sessionInfo.isHomeplusOrEtrace() &&
                <Box
                    style={{
                        paddingTop: 5, display: 'flex', justifyContent: 'space-between'
                    }}
                >
                    <Tooltip
                        title={
                            editorState.changed
                            ? "임시 저장된 데이터를 전송합니다."
                            :
                            item.reqStateFlag === TopCode.ReqStateFlag.TsrIssued
                            ?
                            "TSR 발행 상태이므로 발행 취소합니다."
                            :
                            "상차내역, 집기 등의 데이터를 편집하고 저장할 수 있습니다."
                        }
                    >
                        <Button
                            size={AppWord.SMALL}
                            variant={hovered ? AppStyle.mui.contained : AppStyle.mui.outlined}
                            color={AppStyle.mui.primary}
                            disabled={loadingAny}
                            onClick={onClickSave}
                        >
                            저장
                        </Button>
                    </Tooltip>
                    <Tooltip
                        title={
                            item.reqStateFlag === TopCode.ReqStateFlag.TsrNotIssued ?
                                "TSR 미발행 상태입니다. 발행 후 출력합니다." :
                                item.reqStateFlag === TopCode.ReqStateFlag.TsrIssued ?
                                    "TSR 발행 상태입니다. 즉시 출력합니다." :
                                    "TSR 저장이 필요합니다."
                        }
                    >
                        <span>
                            <Button
                                size={AppWord.SMALL}
                                variant={hovered ? AppStyle.mui.contained : AppStyle.mui.outlined}
                                color={AppStyle.mui.inherit}
                                disabled={!canPrintTsr || loadingAny}
                                onClick={onClickPrintTsr}
                            >
                                TSR
                            </Button>
                        </span>
                    </Tooltip>
                    <Tooltip
                        title={
                            item.reqStateFlag === TopCode.ReqStateFlag.TsrIssued ?
                                "출차 메시지 보내기" : "TSR 발행 후 메시지를 보낼 수 있습니다."
                        }
                    >
                        <span>
                            <Button
                                size={AppWord.SMALL}
                                variant={hovered ? AppStyle.mui.contained : AppStyle.mui.outlined}
                                color={AppStyle.mui.inherit}
                                disabled={!canSendMessageToLeave || loadingAny}
                                onClick={onClickSendMessageForDeparture}
                            >
                                메시지
                            </Button>
                        </span>
                    </Tooltip>
                </Box>}
            {
                editorState.open && editorState.data &&
                <TransPlanTsrEditorGroup
                    open={editorState.open}
                    item={editorState.data}
                    tabSelected={editorState.tab}
                    onClose={onRequestCloseGroupEditor}
                    onRequestSave={onRequestSaveInMemory}
                />}
        </HochaContainer>
    )
}